import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './primitives';

/**
 * Native modal dialog: focus trap, Escape and backdrop click handled by the
 * platform. `onClose` is called for Escape/backdrop; pass `dismissible={false}`
 * while work is running that must not be interrupted by accident.
 */
export function Dialog({
  open,
  onClose,
  dismissible = true,
  labelledBy,
  className,
  children,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly dismissible?: boolean;
  readonly labelledBy: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);
  return (
    <dialog
      ref={dialog}
      aria-labelledby={labelledBy}
      onCancel={(event) => {
        event.preventDefault();
        if (dismissible) onClose();
      }}
      onClick={(event) => dismissible && event.target === dialog.current && onClose()}
      className={`m-auto w-[calc(100%-2rem)] rounded-3xl bg-panel p-0 text-fg shadow-float ring-1 ring-line backdrop:bg-black/55 ${className ?? 'max-w-md'}`}
    >
      {open && children}
    </dialog>
  );
}

/** Asks before an action that throws work away. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <Dialog open={open} onClose={onCancel} labelledBy={id} className="max-w-sm">
      <div className="space-y-5 p-6">
        <div className="space-y-2">
          <h2 id={id} className="text-lg font-semibold tracking-[-0.02em] text-fg">
            {title}
          </h2>
          <p className="text-sm leading-relaxed text-fg-muted">{body}</p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} autoFocus>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" className="bg-danger/10" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
